import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useClickOutside } from "../Hooks/useClickOutside";
import { useLockBodyScroll } from "../Hooks/useLockBodyScroll";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiTrendingUp } from "react-icons/fi";
import useProductVarietiesCache from "../Hooks/useProductVarietiesCache";

const SearchModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const [animate, setAnimate] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);

  // Lock background scroll when modal is active
  useLockBodyScroll(show);

  // ✅ Same cache hook as the navbar — no duplicate fetch
  const { search: searchVarieties, loading } = useProductVarietiesCache();

  const domNode = useClickOutside(() => handleClose());

  useEffect(() => {
    if (show) {
      setTimeout(() => setAnimate(true), 10);
      loadRecentSearches();
    }
  }, [show]);

  // ✅ Frontend fuzzy search — runs instantly, no API call
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length >= 1) {
      const searchedResults = searchVarieties(trimmed);
      setResults(searchedResults || []);
    } else {
      setResults([]);
    }
  }, [query]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const loadRecentSearches = () => {
    const recent = localStorage.getItem("recentSearches");
    if (recent) setRecentSearches(JSON.parse(recent));
  };

  const saveToRecentSearches = (searchTerm) => {
    let recent = recentSearches.filter((item) => item !== searchTerm);
    recent.unshift(searchTerm);
    recent = recent.slice(0, 5);
    setRecentSearches(recent);
    localStorage.setItem("recentSearches", JSON.stringify(recent));
  };

  const handleVarietyClick = (variety) => {
    saveToRecentSearches(variety.varietyName);
    const productId = variety.productId?._id;
    const subCategoryId = variety.productId?.subCategoryId?._id;
    const categoryId = variety.productId?.subCategoryId?.categoryId?._id;
    handleClose();
    navigate(
      `/products?category=${categoryId}&subCategory=${subCategoryId}&product=${productId}&variety=${variety._id}`
    );
  };

  const handleTrendingClick = (item) => setQuery(item);
  const handleRecentClick = (item) => setQuery(item);

  // ✅ Submit now just saves to recent (search already running reactively)
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) saveToRecentSearches(query.trim());
  };

  if (!show) return null;

  return createPortal(
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      className={`fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
    >
      <div
        ref={domNode}
        className={`w-full max-w-2xl mt-12 sm:mt-20 px-4 transition-all duration-300 ${animate ? "translate-y-0 scale-100" : "-translate-y-10 scale-95"
          }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* SEARCH INPUT */}
          <form
            onSubmit={handleSearchSubmit}
            className="flex items-center gap-3 border-b border-gray-200 px-5 py-4"
          >
            <FiSearch className="text-xl text-gray-400" />
            <input
              autoFocus
              type="text"
              placeholder="Search product varieties..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 outline-none text-base"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setResults([]);
                }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                ✖
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition ml-2"
            >
              close
            </button>
          </form>

          {/* CONTENT */}
          <div className="max-h-[60vh] sm:max-h-[70vh] overflow-y-auto overscroll-contain p-5">
            {/* LOADING (only shown while initial cache fetch is in progress) */}
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primaryColor" />
              </div>
            )}

            {/* SEARCH RESULTS */}
            {!loading && query.trim().length >= 1 && results.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wide">
                  Search Results ({results.length})
                </p>
                {results.map((variety) => (
                  <div
                    key={variety._id}
                    onClick={() => handleVarietyClick(variety)}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-primaryColor/5 cursor-pointer transition group"
                  >
                    <img
                      src={variety.img}
                      alt={variety.varietyName}
                      className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm text-gray-900 group-hover:text-primaryColor transition leading-snug break-words">
                        {variety.varietyName}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {variety.productId?.name} {variety.grade ? `• ${variety.grade}` : ""}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {variety.productId?.subCategoryId?.categoryId?.name} →{" "}
                        {variety.productId?.subCategoryId?.name}
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      MOQ: {variety.minimum_MOQ} {variety.standardizedUNIT}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* NO RESULTS */}
            {!loading && query.trim().length >= 1 && results.length === 0 && (
              <div className="text-center py-12">
                <div className="text-4xl mb-3">🔍</div>
                <p className="text-gray-600 font-medium">No results found</p>
                <p className="text-sm text-gray-500 mt-1">
                  Try searching with different keywords
                </p>
              </div>
            )}

            {/* RECENT SEARCHES */}
            {!query && recentSearches.length > 0 && (
              <div className="mb-6">
                <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wide flex items-center gap-2">
                  <FiSearch className="text-sm" />
                  Recent Searches
                </p>
                <div className="space-y-1">
                  {recentSearches.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => handleRecentClick(item)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm text-gray-700 transition flex items-center gap-2"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchModal;